/**
 * Enterprise Authorization Service
 * Handles RBAC (Role-Based Access Control) and permissions
 */

import apiService from './apiClient';
import secureStorage from '../utils/secureStorage';
import logger from '../utils/logger';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ModulePermission {
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canUpdate: boolean;
  canImport: boolean;
  canExport: boolean;
}

export interface AuthModule {
  module_id: string;
  module_name: string;
  module_type: string;
  icon: string;
  route: string;
  is_sub_module: boolean;
  parent_module_id: string | null;
  sequence: number;
  permissions: ModulePermission;
  sub_modules: AuthModule[];
}

export interface ModulesResponse {
  success: boolean;
  message: string;
  data: {
    modules: AuthModule[];
    total_modules: number;
    user_role: string;
    interface: 'web' | 'app';
  };
}

// ============================================================================
// AUTHORIZATION SERVICE
// ============================================================================

class AuthorizationService {
  private readonly BASE_PATH = '/web/authorization';
  private modulesCache: AuthModule[] | null = null;
  private permissionsMap: Map<string, ModulePermission> | null = null;
  private lastFetchTime: number | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all assigned modules with permissions for current user
   */
  async getAssignedModules(forceRefresh: boolean = false): Promise<AuthModule[]> {
    try {
      // Check cache
      if (!forceRefresh && this.isCacheValid()) {
        logger.debug('Returning cached modules');
        return this.modulesCache!;
      }

      const response = await apiService.get<ModulesResponse>(
        `${this.BASE_PATH}/modules`
      );

      if (response.success && response.data) {
        this.modulesCache = response.data.modules;
        this.permissionsMap = this.buildPermissionsMap(response.data.modules);
        this.lastFetchTime = Date.now();

        // Cache in secure storage
        secureStorage.setItem('modules', JSON.stringify(response.data.modules));

        logger.info('Modules fetched successfully', {
          totalModules: response.data.total_modules,
          userRole: response.data.user_role,
        });

        return response.data.modules;
      }

      throw new Error('Failed to fetch modules');
    } catch (error: any) {
      logger.error('Get assigned modules failed', error);

      // Try to load from cache if available
      if (this.modulesCache) {
        logger.warn('API failed, using cached modules');
        return this.modulesCache;
      }

      // Try to load from secure storage
      try {
        const cachedModules = secureStorage.getItem('modules');
        if (cachedModules) {
          logger.warn('Loading modules from secure storage');
          const modules = JSON.parse(cachedModules);
          this.modulesCache = modules;
          this.permissionsMap = this.buildPermissionsMap(modules);
          return modules;
        }
      } catch (storageError) {
        logger.error('Failed to load modules from storage', storageError);
      }

      throw error;
    }
  }

  /**
   * Check if user has permission for a specific module and action
   */
  async hasPermission(
    moduleRoute: string,
    action: keyof ModulePermission = 'canView'
  ): Promise<boolean> {
    try {
      // Ensure modules are loaded
      if (!this.modulesCache) {
        await this.getAssignedModules();
      }

      if (!this.permissionsMap) {
        return false;
      }

      const permission = this.permissionsMap.get(moduleRoute);
      if (!permission) {
        logger.warn(`No permission found for module: ${moduleRoute}`);
        return false;
      }

      return permission[action];
    } catch (error) {
      logger.error('hasPermission check failed', error);
      return false;
    }
  }

  /**
   * Check if user can view a module
   */
  async canView(moduleRoute: string): Promise<boolean> {
    return this.hasPermission(moduleRoute, 'canView');
  }

  /**
   * Check if user can add in a module
   */
  async canAdd(moduleRoute: string): Promise<boolean> {
    return this.hasPermission(moduleRoute, 'canAdd');
  }

  /**
   * Check if user can edit in a module
   */
  async canEdit(moduleRoute: string): Promise<boolean> {
    return this.hasPermission(moduleRoute, 'canEdit');
  }

  /**
   * Check if user can delete in a module
   */
  async canDelete(moduleRoute: string): Promise<boolean> {
    return this.hasPermission(moduleRoute, 'canDelete');
  }

  /**
   * Check if user can import in a module
   */
  async canImport(moduleRoute: string): Promise<boolean> {
    return this.hasPermission(moduleRoute, 'canImport');
  }

  /**
   * Check if user can export in a module
   */
  async canExport(moduleRoute: string): Promise<boolean> {
    return this.hasPermission(moduleRoute, 'canExport');
  }

  /**
   * Get permissions for a specific module
   */
  async getModulePermissions(moduleRoute: string): Promise<ModulePermission | null> {
    try {
      // Ensure modules are loaded
      if (!this.modulesCache) {
        await this.getAssignedModules();
      }

      if (!this.permissionsMap) {
        return null;
      }

      return this.permissionsMap.get(moduleRoute) || null;
    } catch (error) {
      logger.error('getModulePermissions failed', error);
      return null;
    }
  }

  /**
   * Get navigation menu from modules
   */
  async getNavigationMenu(): Promise<AuthModule[]> {
    try {
      const modules = await this.getAssignedModules();

      if (!modules || !Array.isArray(modules)) return [];

      // Filter only top-level modules (not sub-modules) with view permission
      const topLevelModules = modules.filter(m => !m.is_sub_module && m.permissions?.canView);

      // Sort by sequence
      return topLevelModules.sort((a, b) => a.sequence - b.sequence);
    } catch (error) {
      logger.error('getNavigationMenu failed', error);
      return [];
    }
  }

  /**
   * Check if user has any of the specified permissions
   */
  async hasAnyPermission(
    moduleRoute: string,
    actions: Array<keyof ModulePermission>
  ): Promise<boolean> {
    for (const action of actions) {
      const has = await this.hasPermission(moduleRoute, action);
      if (has) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if user has all of the specified permissions
   */
  async hasAllPermissions(
    moduleRoute: string,
    actions: Array<keyof ModulePermission>
  ): Promise<boolean> {
    for (const action of actions) {
      const has = await this.hasPermission(moduleRoute, action);
      if (!has) {
        return false;
      }
    }
    return true;
  }

  /**
   * Clear cached modules (call on logout)
   */
  clearCache(): void {
    this.modulesCache = null;
    this.permissionsMap = null;
    this.lastFetchTime = null;
    secureStorage.removeItem('modules');
    logger.debug('Authorization cache cleared');
  }

  /**
   * Build a map of route -> permissions for fast lookup
   */
  private buildPermissionsMap(modules: AuthModule[]): Map<string, ModulePermission> {
    const map = new Map<string, ModulePermission>();

    const processModule = (module: AuthModule) => {
      // Defensive check for permissions
      if (module.permissions) {
        map.set(module.route, module.permissions);
      }

      // Process sub-modules recursively
      if (module.sub_modules && module.sub_modules.length > 0) {
        module.sub_modules.forEach(processModule);
      }
    };

    if (modules && Array.isArray(modules)) {
      modules.forEach(processModule);
    }

    return map;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    if (!this.modulesCache || !this.lastFetchTime) {
      return false;
    }

    const now = Date.now();
    const elapsed = now - this.lastFetchTime;

    return elapsed < this.CACHE_DURATION;
  }

  /**
   * Filter modules by permission
   */
  filterModulesByPermission(
    modules: AuthModule[],
    permission: keyof ModulePermission
  ): AuthModule[] {
    if (!modules || !Array.isArray(modules)) return [];
    return modules.filter(module => module.permissions && module.permissions[permission]);
  }

  /**
   * Get all accessible routes
   */
  async getAccessibleRoutes(): Promise<string[]> {
    try {
      const modules = await this.getAssignedModules();
      const routes: string[] = [];

      if (!modules || !Array.isArray(modules)) return routes;

      const extractRoutes = (module: AuthModule) => {
        if (module.permissions?.canView) {
          routes.push(module.route);
        }

        // Extract sub-module routes
        if (module.sub_modules && module.sub_modules.length > 0) {
          module.sub_modules.forEach(extractRoutes);
        }
      };

      modules.forEach(extractRoutes);

      return routes;
    } catch (error) {
      logger.error('getAccessibleRoutes failed', error);
      return [];
    }
  }

  /**
   * Check if route is accessible
   */
  async isRouteAccessible(route: string): Promise<boolean> {
    try {
      const accessibleRoutes = await this.getAccessibleRoutes();

      // Exact match
      if (accessibleRoutes.includes(route)) {
        return true;
      }

      // Wildcard match (e.g., /sales/* matches /sales/orders)
      return accessibleRoutes.some(accessibleRoute => {
        if (accessibleRoute.endsWith('/*')) {
          const prefix = accessibleRoute.slice(0, -2);
          return route.startsWith(prefix);
        }
        return false;
      });
    } catch (error) {
      logger.error('isRouteAccessible failed', error);
      return false;
    }
  }
}

// Create and export singleton instance
const authorizationService = new AuthorizationService();
export default authorizationService;
